import React, { useEffect, useRef, useState } from "react";
import { Search, MapPin, Loader2, LocateFixed, Home, Briefcase, Clock } from "lucide-react";
import { mapsApi, addressApi, errorMessage } from "../../lib/api";
import { toast } from "sonner";

// Google Places autocomplete input (no map). onSelect({ address, lat, lng }).
// value is the current text; onTextChange keeps the parent in sync for manual typing.
// "Detect my location" fills the field from GPS; onDetect({lat,lng,address}) lets the
// parent refresh nearby results (e.g. nearby drivers / restaurants).
// showQuick surfaces the customer's saved (Home/Work) + recent places at the top of the
// dropdown before they start typing.
export default function AddressAutocomplete({ value, onTextChange, onSelect, onDetect, allowDetect = true, showQuick = true, placeholder = "Search your address…", testId = "address-autocomplete", className = "" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [saved, setSaved] = useState([]);
  const [recent, setRecent] = useState([]);
  const sessionRef = useRef(Math.random().toString(36).slice(2));
  const debounceRef = useRef(null);

  useEffect(() => { mapsApi.config().then((c) => setEnabled(c.maps_enabled)).catch(() => {}); }, []);
  useEffect(() => {
    if (!showQuick) return;
    addressApi.list().then((l) => setSaved((l || []).filter((a) => Number.isFinite(a.lat)).slice(0, 4))).catch(() => {});
    addressApi.recentPlaces(4).then((p) => setRecent(p || [])).catch(() => {});
  }, [showQuick]);

  const choose = ({ address, lat, lng }) => { onTextChange(address); onSelect({ address, lat, lng }); if (onDetect && lat != null) onDetect({ lat, lng, address }); setOpen(false); };

  const detect = () => {
    if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
    setDetecting(true);
    const onOk = async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      let address = "Current location";
      try { const g = await mapsApi.reverse(lat, lng); if (g?.address) address = g.address; } catch { /* ignore */ }
      onTextChange(address);
      onSelect({ address, lat, lng });
      if (onDetect) onDetect({ lat, lng, address });
      setOpen(false);
      setDetecting(false);
    };
    const onErr = (err) => { setDetecting(false); toast.error(err?.code === 1 ? "Location permission denied — enable it in your browser settings" : "Couldn't detect your location. Try again or type your address"); };
    // Quick high-accuracy attempt, then a relaxed retry so slow/desktop fixes still resolve.
    navigator.geolocation.getCurrentPosition(
      onOk,
      () => navigator.geolocation.getCurrentPosition(onOk, onErr, { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  };

  const run = (text) => {
    onTextChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await mapsApi.autocomplete(text, sessionRef.current); setSuggestions(r.suggestions || []); setOpen(true); }
      catch { setSuggestions([]); } finally { setLoading(false); }
    }, 300);
  };

  const pick = async (s) => {
    setOpen(false);
    onTextChange(s.text);
    try {
      const p = await mapsApi.place(s.place_id);
      onSelect({ address: p.address || s.text, lat: Number.isFinite(p.lat) ? p.lat : null, lng: Number.isFinite(p.lng) ? p.lng : null });
    } catch { onSelect({ address: s.text, lat: null, lng: null }); }
    sessionRef.current = Math.random().toString(36).slice(2);
  };

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
      <input
        type="text"
        value={value}
        onChange={(e) => run(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={enabled ? placeholder : "Type your address"}
        className="h-11 w-full rounded-xl border border-surface-300 bg-white pl-10 pr-9 text-sm text-ink outline-none focus:border-brand"
        data-testid={`${testId}-input`}
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ink-muted" />}
      {open && (allowDetect || suggestions.length > 0 || (showQuick && (saved.length > 0 || recent.length > 0))) && (
        <ul className="absolute z-[1000] mt-1 max-h-72 w-full overflow-auto rounded-xl border border-surface-300 bg-white shadow-lg" data-testid={`${testId}-suggestions`}>
          {allowDetect && (
            <li>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); detect(); }} disabled={detecting} className="flex w-full items-center gap-2 border-b border-surface-200 px-3 py-2.5 text-left text-sm font-semibold text-brand hover:bg-brand-50 disabled:opacity-60" data-testid={`${testId}-locate-option`}>
                {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />} Use my current location
              </button>
            </li>
          )}
          {showQuick && value.trim().length < 3 && saved.map((a) => {
            const Ic = (a.label || "").toLowerCase() === "home" ? Home : (a.label || "").toLowerCase() === "work" ? Briefcase : MapPin;
            return (
              <li key={a.address_id}>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); choose({ address: a.address, lat: a.lat, lng: a.lng }); }} className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-surface-100" data-testid={`${testId}-saved-${a.address_id}`}>
                  <Ic className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" /><span><span className="font-semibold">{a.label}</span> · {a.address}</span>
                </button>
              </li>
            );
          })}
          {showQuick && value.trim().length < 3 && recent.map((r, i) => (
            <li key={`rec-${i}`}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); choose({ address: r.address, lat: r.lat, lng: r.lng }); }} className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-surface-100" data-testid={`${testId}-recent-${i}`}>
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-muted" /><span className="line-clamp-2">{r.address}</span>
              </button>
            </li>
          ))}
          {suggestions.map((s) => (
            <li key={s.place_id}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(s); }} className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-surface-100" data-testid="address-suggestion">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" /><span>{s.text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
